import Modal from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import { trendingService } from '@/services/trending.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowRight, Brain, Calendar, Filter, Loader2, Sparkles, Target, TrendingUp, Zap, Tag } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { TRENDING_TOPICS_PERMISSION, COURSE_GENERATION_PERMISSION } from '@/constants/constant-permissions';

// API response: backend returns a flat list of topics for the selected day.
interface TrendingItem {
  id: string;
  title: string;
  description: string;
  category: string;
  keywords: string[];
  relevance_score: number;
  trend_score: number;
  educational_potential: number;
  source_count: number;
  first_seen_date: string;
  last_updated: string;
  related_topics: string[];
  sources: any[];
}

interface TrendingApiResponse {
  date: string;
  topics: TrendingItem[];
  total_count: number;
  last_modified?: string;
  discovery_metadata?: {
    total_topics_discovered?: number;
    coverage_areas_searched?: string[];
    trending_period?: string;
    search_timestamp?: string;
    processing_time_ms?: number;
    tenant_timezone?: string;
  };
  overall_stats?: {
    total_topics: number;
    high_relevance_count: number;
    avg_educational_potential: number;
  };
}

const sortByOptions = [
  { value: 'trendScore', label: 'Trend Score' },
  { value: 'educational', label: 'Educational Potential' },
  { value: 'relevance', label: 'Relevance' },
  { value: 'title', label: 'Name' },
];

const getTodayYmd = () => format(new Date(), 'yyyy-MM-dd');

export default function TrendingCourseList() {
  usePermissionRouteAccess([
    TRENDING_TOPICS_PERMISSION.TRENDING_TOPICS_READ,
    TRENDING_TOPICS_PERMISSION.TRENDING_TOPICS_WRITE,
  ]);
  const canWrite = useHasPermission([TRENDING_TOPICS_PERMISSION.TRENDING_TOPICS_WRITE]);
  const canGenerate = useHasPermission([COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE]);

  const [selectedDate, setSelectedDate] = useState<string>(getTodayYmd());
  // SearchInputDebounced fires onChange already debounced (300ms by default), so this
  // state always holds the debounced value — no separate useDebounce needed.
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedKeywordsModal, setSelectedKeywordsModal] = useState<{ keywords: string[], title: string } | null>(null);
  const [selectedRelatedTopicsModal, setSelectedRelatedTopicsModal] = useState<{ topics: string[], title: string } | null>(null);

  // Discovery runs asynchronously on the backend (Tavily searches + LLM, ~30-60s). After
  // triggering it we poll the list endpoint until new topics appear, instead of forcing a
  // manual reload. `isPolling` keeps the Discover button disabled for the WHOLE discovery
  // window (not just the trigger request) so the user can't fire duplicate runs and waste
  // search-API credits.
  const POLL_INTERVAL_MS = 30000;  // check every 30s
  const POLL_TIMEOUT_MS = 180000;  // give up after 3 minutes
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The backend `last_modified` is the GLOBAL latest update across all trending docs, so we
  // can detect "discovery finished" by watching it advance past this baseline.
  const baselineModifiedRef = useRef<string | null>(null);

  // Tracks what triggered the latest query refetch so we only show the search box's loading
  // spinner for actual search fetches — not for date changes (which also refetch the query).
  const fetchSourceRef = useRef<'search' | 'date' | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    setIsPolling(false);
  };

  // Always clear timers on unmount to avoid leaks / setState-after-unmount warnings.
  useEffect(() => () => stopPolling(), []);

  const { control: filterControl, watch: watchFilter } = useForm({
    defaultValues: {
      sortBy: 'trendScore',
    }
  });

  const sortBy = watchFilter('sortBy');

  const { data, isLoading, isFetching, error } = useQuery<TrendingApiResponse>({
    queryKey: ['trendingTopics', selectedDate, searchTerm],
    queryFn: () => trendingService.getTrendingTopics({
      date: selectedDate,
      ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
    }),
    placeholderData: (prev) => prev,
  });

  // Poll today's topics until the global `last_modified` advances past the baseline taken
  // when discovery started. New topics always land on today's (tenant-local) date, so we
  // poll today regardless of which date the user is currently viewing.
  const startPolling = () => {
    stopPolling();
    setIsPolling(true);
    const today = getTodayYmd();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await trendingService.getTrendingTopics({ date: today });
        const newModified = res?.last_modified ?? null;
        // Detected: last_modified changed (or first-ever data arrived).
        if (newModified && newModified !== baselineModifiedRef.current) {
          stopPolling();
          // Jump to today's date so the freshly discovered topics are visible, and clear
          // any active search so nothing is filtered out.
          setSelectedDate(today);
          setSearchTerm('');
          queryClient.invalidateQueries({ queryKey: ['trendingTopics'] });
          const count = res?.topics?.length ?? 0;
          toast.success(count > 0 ? `${count} new topics discovered.` : 'New topics discovered.');
        }
      } catch {
        // Ignore transient poll errors; the timeout below is the safety net.
      }
    }, POLL_INTERVAL_MS);

    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      toast('Discovery is taking longer than usual. Please check back shortly.', { icon: '⏳' });
    }, POLL_TIMEOUT_MS);
  };

  const discoveryMutation = useMutation({
    mutationFn: () => trendingService.triggerDiscovery({}),
    onSuccess: async (response: any) => {
      const message = response?.message || 'Discovery started. This usually takes under a minute.';
      toast.success(message);
      // Capture the current global last_modified as the baseline, then start polling for it
      // to advance. A fresh fetch (rather than the cached `data`) keeps the baseline accurate
      // even when the user is viewing a past date.
      try {
        const baseline = await trendingService.getTrendingTopics({ date: getTodayYmd() });
        baselineModifiedRef.current = baseline?.last_modified ?? null;
      } catch {
        baselineModifiedRef.current = null;
      }
      startPolling();
    },
    onError: (error: any) => {
      console.error('Discovery error:', error);
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to discover trending topics. Please try again.'
      );
    },
  });

  // Disabled/spinning for the whole discovery window: the trigger request AND the poll wait.
  const isDiscovering = discoveryMutation.isPending || isPolling;

  // Show a single toast when the fetch errors. Without useEffect, this would fire on every
  // re-render while `error` is set, spamming the user with duplicate toasts.
  useEffect(() => {
    if (error) toast.error('Failed to fetch trending topics.');
  }, [error]);

  // Backend already filtered by the debounced search term (sent via query param), so
  // here we only sort the returned topics. Sort stays client-side because reordering
  // ~10 items in memory is instant.
  const topicsForDay: TrendingItem[] = useMemo(() => data?.topics ?? [], [data]);

  const filteredTopics: TrendingItem[] = useMemo(() => {
    return [...topicsForDay].sort((a, b) => {
      if (sortBy === 'trendScore') return b.trend_score - a.trend_score;
      if (sortBy === 'educational') return b.educational_potential - a.educational_potential;
      if (sortBy === 'relevance') return b.relevance_score - a.relevance_score;
      return a.title.localeCompare(b.title);
    });
  }, [topicsForDay, sortBy]);

  // Overall (lifetime) stats come from the backend aggregation across ALL days.
  const overallTotalTopics = data?.overall_stats?.total_topics ?? 0;
  const overallHighRelevance = data?.overall_stats?.high_relevance_count ?? 0;
  const overallAvgEducational = data?.overall_stats?.avg_educational_potential ?? 0;

  const getRelevanceLabel = (score: number) => {
    if (score === 4) return { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200' };
    if (score === 3) return { label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-200' };
    if (score === 2) return { label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    return { label: 'Low', color: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  const getTrendScoreColor = (score: number) => {
    if (score >= 90) return 'text-red-600';
    if (score >= 80) return 'text-orange-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-green-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const handleGenerateCourse = (topic: TrendingItem) => {
    navigate('/admin/course/add-course', {
      state: {
        trendingTopic: {
          title: topic.title,
          desc: topic.description,
          description: topic.description,
          category: topic.category,
          keywords: topic.keywords,
          related_topics: topic.related_topics,
          trend_score: topic.trend_score,
          educational_potential: topic.educational_potential,
          relevance_score: topic.relevance_score,
        }
      }
    });
  };

  // With server-side search, topicsForDay already excludes non-matches. We treat
  // an empty active search as "no data for date" and a non-empty active search as
  // "no search match" — same UX as before, the distinction is just inferred client-side now.
  const hasActiveSearch = searchTerm.trim().length > 0;
  const selectedDateLabel = (() => {
    try {
      return format(new Date(selectedDate), 'MMM dd, yyyy');
    } catch {
      return selectedDate;
    }
  })();

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Brain className="w-6 h-6" /></div>
            <div>
              <h1 className="text-2xl font-bold">AI-Suggested Trending Topics</h1>
              <p className="text-purple-100">Discover what's hot based on AI analysis</p>
            </div>
          </div>
          <button
            onClick={() => discoveryMutation.mutate()}
            disabled={!canWrite || isDiscovering}
            title={!canWrite ? 'You do not have permission to discover topics' : undefined}
            className="relative flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-all font-semibold disabled:cursor-not-allowed disabled:opacity-50 shadow-lg overflow-hidden group"
          >
            {isDiscovering && (
              <span className="absolute inset-0 bg-gradient-to-r from-purple-100 via-blue-100 to-purple-100 animate-pulse"></span>
            )}
            <span className="relative flex items-center gap-2">
              {isDiscovering ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="animate-pulse">Discovering...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
                  <span>Discover New Topics</span>
                </>
              )}
            </span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5" /><span className="font-semibold">Total Topics</span></div>
            <div className="text-2xl font-bold">{overallTotalTopics}</div>
            <div className="text-sm text-purple-200">Daily generations (all-time)</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2"><Target className="w-5 h-5" /><span className="font-semibold">High Relevance</span></div>
            <div className="text-2xl font-bold">{overallHighRelevance}</div>
            <div className="text-sm text-purple-200">Critical & High (all-time)</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5" /><span className="font-semibold">Avg Educational</span></div>
            <div className="text-2xl font-bold">{overallAvgEducational}%</div>
            <div className="text-sm text-purple-200">Potential (all-time)</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filter Topics</h2>
        </div>
        {/* Option A: search grows to fill, Date + Sort are fixed-width and grouped on the
            right. No inline labels — placeholders + icons keep it clean. Stacks on mobile. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Primary action: search with a sensible fixed width (not full-row) */}
          <div className="w-full sm:w-72">
            <SearchInputDebounced
              value={searchTerm}
              onChange={(val: string) => { fetchSourceRef.current = 'search'; setSearchTerm(val); }}
              placeholder="Search by title or keyword..."
              debounceDelay={300}
              loading={isFetching && fetchSourceRef.current === 'search'}
              className="w-full"
            />
          </div>

          {/* Right cluster: Date + Sort, fixed width, grouped together. The date input has a
              "Date" label since a native date picker has no meaningful placeholder. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Date:</span>
              <div className="relative flex-1 sm:flex-none">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                <input
                  type="date"
                  value={selectedDate}
                  max={getTodayYmd()}
                  onChange={(e) => { fetchSourceRef.current = 'date'; setSelectedDate(e.target.value || getTodayYmd()); }}
                  aria-label="Filter by date"
                  className="w-full sm:w-44 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select
                name="sortBy"
                control={filterControl}
                options={sortByOptions}
                isSearchable={false}
                placeholder="Sort by"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading trending topics...</p>
          </div>
        </div>
      ) : filteredTopics.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {hasActiveSearch
                ? 'No topics match your search'
                : `No trending topics for ${selectedDateLabel}`}
            </h3>
            <p className="text-gray-600 mb-6">
              {hasActiveSearch
                ? 'Try a different search term or clear the filter.'
                : 'Pick a different date or click "Discover New Topics" to run a fresh discovery.'}
            </p>
            {!hasActiveSearch && (
              <button
                onClick={() => discoveryMutation.mutate()}
                disabled={!canWrite || isDiscovering}
                title={!canWrite ? 'You do not have permission to discover topics' : undefined}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDiscovering ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Discovering...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Discover New Topics</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTopics.map((topic: TrendingItem) => {
            const relevance = getRelevanceLabel(topic.relevance_score);
            return (
              <div key={topic.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all overflow-visible relative">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{topic.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${relevance.color}`}>
                          {relevance.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="px-2 py-1 bg-white rounded-full">{topic.category}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(topic.last_updated), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-1">
                        <TrendingUp className={`w-5 h-5 ${getTrendScoreColor(topic.trend_score)}`} />
                        <span className={`text-xl font-bold ${getTrendScoreColor(topic.trend_score)}`}>
                          {topic.trend_score}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">Trend</span>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <p className="text-sm text-gray-700 mb-4">{topic.description}</p>

                  <div className="space-y-2 mb-4">
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600">Educational Potential</span>
                        <span className="font-medium text-gray-900">{topic.educational_potential}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getScoreColor(topic.educational_potential)}`}
                          style={{ width: `${topic.educational_potential}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {topic.keywords && topic.keywords.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Keywords</p>
                      <div className="flex flex-wrap gap-1">
                        {topic.keywords.slice(0, 5).map((keyword: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                            {keyword}
                          </span>
                        ))}
                        {topic.keywords.length > 5 && (
                          <button
                            onClick={() => setSelectedKeywordsModal({ keywords: topic.keywords, title: topic.title })}
                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                          >
                            +{topic.keywords.length - 5} more
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {topic.related_topics && topic.related_topics.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Related Topics</p>
                      <div className="flex flex-wrap gap-1">
                        {topic.related_topics.slice(0, 3).map((related: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            {related}
                          </span>
                        ))}
                        {topic.related_topics.length > 3 && (
                          <button
                            onClick={() => setSelectedRelatedTopicsModal({ topics: topic.related_topics, title: topic.title })}
                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                          >
                            +{topic.related_topics.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                    </div>
                    <button
                      onClick={() => canGenerate && handleGenerateCourse(topic)}
                      disabled={!canGenerate}
                      title={!canGenerate ? 'You do not have permission to generate courses' : undefined}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Zap className="w-4 h-4" />
                      <span>Build Course</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={!!selectedKeywordsModal}
        onClose={() => setSelectedKeywordsModal(null)}
        title="Keywords"
        subtitle={selectedKeywordsModal ? `${selectedKeywordsModal.keywords.length} keywords for "${selectedKeywordsModal.title}"` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedKeywordsModal(null)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedKeywordsModal && (
          <div className="space-y-3">
            {selectedKeywordsModal.keywords.map((keyword: string, idx: number) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{keyword}</h3>
                </div>
                <Tag className="w-5 h-5 text-purple-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!selectedRelatedTopicsModal}
        onClose={() => setSelectedRelatedTopicsModal(null)}
        title="Related Topics"
        subtitle={selectedRelatedTopicsModal ? `${selectedRelatedTopicsModal.topics.length} related topics for "${selectedRelatedTopicsModal.title}"` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedRelatedTopicsModal(null)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedRelatedTopicsModal && (
          <div className="space-y-3">
            {selectedRelatedTopicsModal.topics.map((topic: string, idx: number) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{topic}</h3>
                </div>
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
